import React, { useState, useEffect, useRef } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import {
  Box,
  Card,
  CardContent,
  Button,
  Typography,
  Container,
  Alert,
  CircularProgress,
  Grid,
  Chip,
  Divider,
  Stack,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  useTheme,
  alpha,
  IconButton,
  Tooltip,
  TextField,
} from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { EnhancedTabs, EnhancedTab } from '../components/common/EnhancedTabs';
import { styled } from '@mui/material/styles';
import stopCircleFill from '@iconify/icons-eva/stop-circle-fill';
import checkmarkCircle2Fill from '@iconify/icons-eva/checkmark-circle-2-fill';
import closeCircleFill from '@iconify/icons-eva/close-circle-fill';
import downloadFill from '@iconify/icons-eva/download-fill';
import batteryFill from '@iconify/icons-eva/battery-fill';
import flashFill from '@iconify/icons-eva/flash-fill';
import fileTextFill from '@iconify/icons-eva/file-text-fill';
import bellFill from '@iconify/icons-eva/bell-fill';
import { Icon } from '@iconify/react';
import { fDateTime } from 'src/utils/formatTime';
import { getActiveTransaction, stopTransaction, getChargerDetails, sendOTPToMobile, verifyOTP } from 'src/services/activeTransactionService';
import { useDomainConfig } from 'src/hooks/useDomainConfig';
import Page from '../components/Page';
import Logo from '../components/Logo';
import jsPDF from 'jspdf';

// Connector type mapping
const CONNECTOR_TYPES = [
  { type: "CCS Type 2", value: "connector1", id: 1 },
  { type: "Type 2", value: "connector2", id: 2 },
  { type: "CHAdeMo", value: "connector3", id: 3 },
  { type: "Type 1", value: "connector4", id: 4 },
  { type: "Type 1 AC", value: "connector5", id: 5 },
  { type: "3 Pin", value: "connector6", id: 6 },
  { type: "GBT", value: "connector7", id: 7 },
  { type: "CCS Type 1", value: "ccstype1", id: 8 },
  { type: "CCS/SAE", value: "ccssae", id: 9 },
  { type: "Three Phase", value: "threephase", id: 10 },
  { type: "Wall", value: "wall", id: 11 },
  { type: "J1772", value: "j1772", id: 12 },
  { type: "IEC60309", value: "ie60309", id: 13 },
  { type: "Type 6", value: "connector8", id: 14 },
  { type: "Type 7", value: "connector9", id: 15 },
];

// Function to get connector type name from value
const getConnectorTypeName = (connectorValue) => {
  const connector = CONNECTOR_TYPES.find(c => c.value === connectorValue);
  return connector ? connector.type : connectorValue;
};

// Function to get connector image path
const getConnectorImagePath = (connectorValue) => {
  return `/connectors/${connectorValue}.png`;
};

const RootStyle = styled(Page)(({ theme }) => ({
  minHeight: '100vh',
  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
  padding: theme.spacing(1),
  position: 'relative',
  overflow: 'hidden',
  '&::before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: 'radial-gradient(circle at 20% 50%, rgba(102, 126, 234, 0.1) 0%, transparent 50%), radial-gradient(circle at 80% 80%, rgba(118, 75, 162, 0.1) 0%, transparent 50%)',
    pointerEvents: 'none',
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.5),
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
  },
}));

const SearchCard = styled(Card)(({ theme }) => ({
  width: "100%",
  maxWidth: "100%",
  display: "flex",
  flexDirection: "column",
  justifyContent: "center",
  margin: theme.spacing(1, 'auto'),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
  borderRadius: theme.shape.borderRadius * 2,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 24px rgba(0, 0, 0, 0.15)',
  },
  [theme.breakpoints.up('sm')]: {
    maxWidth: 600,
    margin: theme.spacing(2, 'auto'),
    borderRadius: theme.shape.borderRadius * 3,
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
    },
  },
  [theme.breakpoints.up('md')]: {
    maxWidth: 800,
  }
}));

const LogoContainer = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  marginBottom: theme.spacing(3),
  paddingTop: theme.spacing(1),
  position: 'relative',
  zIndex: 1,
  '& .logo': {
    maxWidth: 120,
    height: 'auto',
    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08))',
    transition: 'transform 0.3s ease',
    '&:hover': {
      transform: 'scale(1.02)',
    },
    '& img': {
      width: '100%',
      height: 'auto',
      maxHeight: 36,
      objectFit: 'contain',
    }
  },
  [theme.breakpoints.up('sm')]: {
    marginBottom: theme.spacing(4),
    '& .logo': {
      maxWidth: 140,
      '& img': {
        maxHeight: 40,
      }
    }
  },
  [theme.breakpoints.up('md')]: {
    marginBottom: theme.spacing(4),
    '& .logo': {
      maxWidth: 160,
      '& img': {
        maxHeight: 44,
      }
    }
  }
}));

const InfoCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  boxShadow: `0 4px 16px ${theme.palette.grey[500_24]}`,
  borderRadius: theme.shape.borderRadius * 2,
  background: `linear-gradient(135deg, #1A73E8 0%, #34A853 50%, #0D47A1 100%)`,
  color: '#ffffff',
  position: 'relative',
  overflow: 'hidden',
  border: `1px solid rgba(76, 175, 80, 0.3)`,
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  '&:before': {
    content: '""',
    position: 'absolute',
    top: 0,
    left: 0,
    right: 0,
    bottom: 0,
    background: `linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.15) 50%, transparent 70%)`,
    pointerEvents: 'none',
  },
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 3,
    boxShadow: `0 8px 24px ${theme.palette.grey[500_24]}`,
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: `0 12px 32px rgba(26, 115, 232, 0.4)`,
    },
  },
}));

const DetailCard = styled(Card)(({ theme }) => ({
  marginTop: theme.spacing(1.5),
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.08)',
  borderRadius: theme.shape.borderRadius * 2,
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.3)',
  transition: 'transform 0.3s ease, box-shadow 0.3s ease',
  [theme.breakpoints.up('sm')]: {
    marginTop: theme.spacing(2),
    borderRadius: theme.shape.borderRadius * 3,
    boxShadow: '0 8px 32px rgba(0, 0, 0, 0.08)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 12px 40px rgba(0, 0, 0, 0.12)',
    },
  },
}));

const GridLabel = styled(Box)(({ theme }) => ({
  padding: theme.spacing(1),
  borderRadius: theme.shape.borderRadius,
  background: alpha(theme.palette.primary.main, 0.03),
  border: `1px solid ${alpha(theme.palette.primary.main, 0.1)}`,
  transition: 'all 0.3s ease',
  height: '100%',
  '& .label': {
    fontSize: '0.65rem',
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(0.25),
    fontWeight: 600,
    textTransform: 'uppercase',
    letterSpacing: '0.3px',
    lineHeight: 1.2,
  },
  '& .value': {
    fontSize: '0.85rem',
    color: theme.palette.text.primary,
    fontWeight: 700,
    lineHeight: 1.3,
    wordBreak: 'break-word',
  },
  [theme.breakpoints.up('sm')]: {
    padding: theme.spacing(1.5),
    borderRadius: theme.shape.borderRadius * 1.5,
  '&:hover': {
    background: alpha(theme.palette.primary.main, 0.06),
    borderColor: alpha(theme.palette.primary.main, 0.2),
    transform: 'translateY(-2px)',
  },
    '& .label': {
      fontSize: '0.7rem',
      marginBottom: theme.spacing(0.5),
    },
    '& .value': {
      fontSize: '1rem',
    },
  },
  [theme.breakpoints.up('md')]: {
    padding: theme.spacing(2),
  '& .label': {
    fontSize: '0.75rem',
    marginBottom: theme.spacing(0.75),
  },
  '& .value': {
    fontSize: '1.1rem',
    },
  },
}));

export default function ActiveTransaction() {
  const location = useLocation();
  const navigate = useNavigate();
  const domainConfig = useDomainConfig();
  const theme = useTheme();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [transactions, setTransactions] = useState({}); // Store transactions per connector
  const [selectedTab, setSelectedTab] = useState(0);
  const selectedConnectorIdRef = useRef(null); // Track selected connector by ID to preserve selection
  const isInitialLoadRef = useRef(true); // Track if this is the initial load
  const prevChargerDetailsRef = useRef(null); // Track previous charger details to detect actual changes
  const [stopDialogOpen, setStopDialogOpen] = useState(false);
  const [stopping, setStopping] = useState(false);
  const [stopError, setStopError] = useState('');
  const [utrNumber, setUtrNumber] = useState('');
  const [utrNumberError, setUtrNumberError] = useState('');
  const [stopConnectorId, setStopConnectorId] = useState(null);
  const [stopTransactionId, setStopTransactionId] = useState(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [chargerDetails, setChargerDetails] = useState(null);
  const [chargerLoading, setChargerLoading] = useState(false);
  const [transactionLoading, setTransactionLoading] = useState({}); // Loading state per connector
  const fetchingRef = useRef(new Set()); // Track which connectors are currently being fetched
  
  // Notification states
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [notificationConnectorId, setNotificationConnectorId] = useState(null);
  const [notificationTransactionId, setNotificationTransactionId] = useState(null);
  const [email, setEmail] = useState('');
  const [emailError, setEmailError] = useState('');
  const [otpSent, setOtpSent] = useState(false);
  const [otp, setOtp] = useState('');
  const [otpError, setOtpError] = useState('');
  const [sendingOtp, setSendingOtp] = useState(false);
  const [verifyingOtp, setVerifyingOtp] = useState(false);
  const [notificationError, setNotificationError] = useState('');
  const [notificationSuccess, setNotificationSuccess] = useState(false);
  
  // Get logo URL from domain config, fallback to default
  const logoUrl = domainConfig?.logoUrl || '/logo/amikCharge.svg';

  // Function to fetch charger details
  const fetchChargerDetails = async (chargerId) => {
    if (!chargerId) return;
    
    setChargerLoading(true);
    setChargerDetails(null); // Clear previous charger details
    try {
      const data = await getChargerDetails(chargerId);
      // Handle both response formats: {success: true, data: {...}} or direct object
      if (data) {
        if (data.success && data.data) {
          setChargerDetails(data.data);
        } else if (data.chargepoint_id || data.chargepoint_pk) {
          // Direct response object (as per sample response format)
          setChargerDetails(data);
        }
      }
    } catch (err) {
      // Don't set error for charger details
      setChargerDetails(null);
    } finally {
      setChargerLoading(false);
    }
  };

  // Function to fetch transaction for a specific connector
  const fetchTransactionForConnector = async (chargerId, connectorId) => {
    try {
      // Convert to string and trim to handle both string and number connector IDs
      const chargerIdStr = String(chargerId || '').trim();
      const connectorIdStr = String(connectorId || '').trim();
      
      if (!chargerIdStr || !connectorIdStr) {
        return null;
      }
      
      const data = await getActiveTransaction(chargerIdStr, connectorIdStr);
      if (data && data.success && data.data) {
        return data.data;
      }
      return null;
    } catch (err) {
      return null;
    }
  };

  // Read query parameters from URL on component mount - only chargerId
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlChargerId = searchParams.get('chargerId');
    
    const fetchAllData = async () => {
      if (!urlChargerId) {
        setHasSearched(true);
        setError('No charger ID provided.');
        return;
      }

      setLoading(true);
      setError('');
      setTransactions({});
      setStopError('');
      setHasSearched(true);
      setTransactionLoading({});
      // Reset refs when fetching new charger
      selectedConnectorIdRef.current = null;
      isInitialLoadRef.current = true;
      prevChargerDetailsRef.current = null;

      try {
        // Fetch charger details first
        await fetchChargerDetails(urlChargerId.trim());
      } catch (err) {
        setError(err.message || 'Failed to fetch charger details.');
      } finally {
        setLoading(false);
      }
    };
    
    fetchAllData();
  }, [location.search]);

  // Fetch transaction for the first connector that is in use when charger details are loaded
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlChargerId = searchParams.get('chargerId');
    
    if (!urlChargerId || !chargerDetails || !chargerDetails.connectors || chargerDetails.connectors.length === 0) {
      return;
    }
    
    const fetchTransactionForFirstInUseConnector = async () => {
      // If charger status is N/A, don't make API calls
      const chargerStatus = chargerDetails.status?.toString().toUpperCase();
      if (chargerStatus === 'N/A' || chargerStatus === 'NA') {
        setTransactions({});
        return;
      }
      
      // Filter connectors that are in use
      const inUseConnectors = chargerDetails.connectors.filter((connector) => {
        const status = connector.status?.toLowerCase() || connector.connectorStatus?.toLowerCase() || '';
        const isInUse = status === 'in use' || 
                       status === 'charging' || 
                       status === 'preparing';
        return isInUse;
      });
      
      // If no connectors are in use, set empty transactions map
      if (inUseConnectors.length === 0) {
        setTransactions({});
        return;
      }
      
      // Get the first connector that is in use
      const firstInUseConnector = inUseConnectors[0];
      const connectorId = firstInUseConnector.connector_id || firstInUseConnector.connectorId || firstInUseConnector.id;
      
      if (!connectorId) {
        return;
      }
      
      // Skip if already fetching this connector
      if (fetchingRef.current.has(connectorId)) {
        return;
      }
      
      // Check if we already have the transaction using functional update to avoid stale state
      let hasTransaction = false;
      setTransactions(prev => {
        if (prev[connectorId]) {
          hasTransaction = true;
        }
        return prev;
      });
      
      if (hasTransaction) {
        return;
      }
      
      // Mark as fetching
      fetchingRef.current.add(connectorId);
      setTransactionLoading(prev => ({ ...prev, [connectorId]: true }));
      
      try {
        // Call public API: /cms/v1/public/transaction/active?chargerId={chargerId}&connectorId={connectorId}
        const transaction = await fetchTransactionForConnector(urlChargerId.trim(), connectorId);
        setTransactionLoading(prev => ({ ...prev, [connectorId]: false }));
        setTransactions(prev => {
          // Only update if we don't already have this transaction
          if (prev[connectorId]) {
            return prev;
          }
          return { ...prev, [connectorId]: transaction };
        });
        fetchingRef.current.delete(connectorId);
      } catch (err) {
        setTransactionLoading(prev => ({ ...prev, [connectorId]: false }));
        fetchingRef.current.delete(connectorId);
      }
    };
    
    fetchTransactionForFirstInUseConnector();
  }, [chargerDetails, location.search]);

  // Additional useEffect to fetch transactions for any connector that is in use but doesn't have a transaction
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlChargerId = searchParams.get('chargerId');
    
    if (!urlChargerId || !chargerDetails || !chargerDetails.connectors || chargerDetails.connectors.length === 0) {
      return;
    }
    
    // If charger status is N/A, don't make API calls
    const chargerStatus = chargerDetails.status?.toString().toUpperCase();
    if (chargerStatus === 'N/A' || chargerStatus === 'NA') {
      return;
    }
    
    // Find connectors that are in use but don't have transactions
    chargerDetails.connectors.forEach((connector) => {
      const status = connector.status?.toLowerCase() || connector.connectorStatus?.toLowerCase() || '';
      const isInUse = status === 'in use' || 
                     status === 'charging' || 
                     status === 'preparing';
      
      if (!isInUse) return;
      
      const connectorId = connector.connector_id || connector.connectorId || connector.id;
      if (!connectorId) return;
      
      // Check if we already have a transaction or are fetching (using functional updates to avoid stale state)
      let shouldFetch = true;
      setTransactions(prev => {
        if (prev[connectorId]) {
          shouldFetch = false;
        }
        return prev;
      });
      
      setTransactionLoading(prev => {
        if (prev[connectorId]) {
          shouldFetch = false;
        }
        return prev;
      });
      
      if (!shouldFetch || fetchingRef.current.has(connectorId)) {
        return;
      }
      
      // Mark as fetching
      fetchingRef.current.add(connectorId);
      setTransactionLoading(prev => ({ ...prev, [connectorId]: true }));
      
      fetchTransactionForConnector(urlChargerId.trim(), connectorId)
        .then((fetchedTransaction) => {
          setTransactionLoading(prev => ({ ...prev, [connectorId]: false }));
          if (fetchedTransaction) {
            setTransactions(prev => ({ ...prev, [connectorId]: fetchedTransaction }));
          }
          fetchingRef.current.delete(connectorId);
        })
        .catch((err) => {
          setTransactionLoading(prev => ({ ...prev, [connectorId]: false }));
          fetchingRef.current.delete(connectorId);
        });
    });
  }, [chargerDetails, location.search]);

  // Auto-refresh data every 60 seconds
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const urlChargerId = searchParams.get('chargerId');
    
    if (!urlChargerId || !hasSearched) {
      return;
    }

    const refreshData = async () => {
      if (!urlChargerId) return;

      try {
        // Refresh charger details first
        setChargerLoading(true);
        const data = await getChargerDetails(urlChargerId.trim());
        let updatedChargerDetails = null;
        
        // Handle both response formats: {success: true, data: {...}} or direct object
        if (data) {
          if (data.success && data.data) {
            updatedChargerDetails = data.data;
          } else if (data.chargepoint_id || data.chargepoint_pk) {
            updatedChargerDetails = data;
          }
        }
        
        setChargerLoading(false);
        
        if (!updatedChargerDetails || !updatedChargerDetails.connectors) {
          return;
        }

        // Update charger details state
        setChargerDetails(updatedChargerDetails);
        
        // Check charger status
        const chargerStatus = updatedChargerDetails.status?.toString().toUpperCase();
        if (chargerStatus === 'N/A' || chargerStatus === 'NA') {
          setTransactions({});
          return;
        }

        // Refresh transactions for all connectors that are in use
        updatedChargerDetails.connectors.forEach((connector) => {
          const status = connector.status?.toLowerCase() || connector.connectorStatus?.toLowerCase() || '';
          const isInUse = status === 'in use' || 
                         status === 'charging' || 
                         status === 'preparing';
          
          if (!isInUse) return;
          
          const connectorId = connector.connector_id || connector.connectorId || connector.id;
          if (!connectorId || fetchingRef.current.has(connectorId)) return;

          // Mark as fetching
          fetchingRef.current.add(connectorId);
          setTransactionLoading(prev => ({ ...prev, [connectorId]: true }));

          fetchTransactionForConnector(urlChargerId.trim(), connectorId)
            .then((fetchedTransaction) => {
              setTransactionLoading(prev => ({ ...prev, [connectorId]: false }));
              if (fetchedTransaction) {
                setTransactions(prev => ({ ...prev, [connectorId]: fetchedTransaction }));
              }
              fetchingRef.current.delete(connectorId);
            })
            .catch((err) => {
              setTransactionLoading(prev => ({ ...prev, [connectorId]: false }));
              fetchingRef.current.delete(connectorId);
            });
        });
      } catch (err) {
        setChargerLoading(false);
        // Silently handle errors during auto-refresh to avoid disrupting the UI
        console.error('Auto-refresh error:', err);
      }
    };

    // Set up interval to refresh every 60 seconds (60000 milliseconds)
    const intervalId = setInterval(refreshData, 30000);

    // Cleanup interval on unmount or when dependencies change
    return () => {
      clearInterval(intervalId);
    };
  }, [location.search, hasSearched]);

  // Helper function to sort connectors the same way as in the render
  const getSortedConnectors = (connectors) => {
    if (!connectors || connectors.length === 0) return [];
    return [...connectors].sort((a, b) => {
      const aId = a.connector_id || a.connectorId || a.id || 0;
      const bId = b.connector_id || b.connectorId || b.id || 0;
      return Number(aId) - Number(bId);
    });
  };

  // Preserve tab selection when charger details are updated during auto-refresh
  useEffect(() => {
    if (!chargerDetails || !chargerDetails.connectors || chargerDetails.connectors.length === 0) {
      return;
    }

    // Get sorted connectors (same order as tabs are rendered)
    const sortedConnectors = getSortedConnectors(chargerDetails.connectors);

    // On initial load, just initialize the ref but don't change tab
    if (isInitialLoadRef.current) {
      // Use sorted connectors to match what's displayed in tabs
      const currentConnector = sortedConnectors[selectedTab];
      if (currentConnector) {
        const connectorId = currentConnector.connector_id || currentConnector.connectorId || currentConnector.id;
        if (connectorId) {
          selectedConnectorIdRef.current = connectorId;
        }
      }
      prevChargerDetailsRef.current = chargerDetails;
      isInitialLoadRef.current = false;
      return; // Don't change tab on initial load
    }

    // On auto-refresh (chargerDetails changed), preserve tab by connector ID
    // Only run preservation logic if we have a tracked connector ID
    if (selectedConnectorIdRef.current) {
      // Find connector in sorted array (same order as tabs)
      const connectorIndex = sortedConnectors.findIndex((connector) => {
        const connectorId = connector.connector_id || connector.connectorId || connector.id;
        return connectorId === selectedConnectorIdRef.current;
      });
      
      if (connectorIndex !== -1) {
        // Found the previously selected connector - preserve that tab
        // Use functional update to ensure we're working with latest state
        setSelectedTab(prevTab => {
          // Only change if different - this prevents unnecessary updates
          if (prevTab !== connectorIndex) {
            return connectorIndex;
          }
          return prevTab; // Keep current tab if it's already correct
        });
        prevChargerDetailsRef.current = chargerDetails;
        return;
      }
      // If connector not found, fall through to bounds check
    }

    // Update previous charger details ref
    prevChargerDetailsRef.current = chargerDetails;

    // Fallback: Only adjust if current tab is out of bounds - otherwise preserve current selection
    const maxTab = sortedConnectors.length - 1;
    setSelectedTab(prevTab => {
      if (prevTab > maxTab) {
        // Only adjust if out of bounds
        return Math.max(0, maxTab);
      }
      // Keep the current tab selection - don't change it
      return prevTab;
    });
  }, [chargerDetails]);

  // Update the selected connector ID ref when tab changes (either manually or programmatically)
  // This runs AFTER tab changes to keep ref in sync
  useEffect(() => {
    if (!chargerDetails || !chargerDetails.connectors || chargerDetails.connectors.length === 0) {
      return;
    }
    
    // Get sorted connectors (same order as tabs are rendered)
    const sortedConnectors = getSortedConnectors(chargerDetails.connectors);
    
    // Ensure selectedTab is within bounds
    const maxTab = sortedConnectors.length - 1;
    const validTab = Math.min(selectedTab, maxTab);
    
    // Use sorted connectors to match what's displayed in tabs
    const currentConnector = sortedConnectors[validTab];
    if (currentConnector) {
      const connectorId = currentConnector.connector_id || currentConnector.connectorId || currentConnector.id;
      if (connectorId && selectedConnectorIdRef.current !== connectorId) {
        // Only update ref if it's different to avoid unnecessary updates
        selectedConnectorIdRef.current = connectorId;
      }
    }
  }, [selectedTab, chargerDetails]);

  const getCurrentTransaction = () => {
    if (!chargerDetails || !chargerDetails.connectors || chargerDetails.connectors.length === 0) {
      return null;
    }
    const currentConnector = chargerDetails.connectors[selectedTab];
    if (!currentConnector) return null;
    const connectorId = currentConnector.connector_id || currentConnector.connectorId || currentConnector.id;
    return connectorId ? transactions[connectorId] || null : null;
  };

  const handleStopClick = (connectorId = null) => {
    // Get the connector ID - use provided one or get from current tab
    let targetConnectorId = connectorId;
    if (!targetConnectorId) {
      const currentConnector = chargerDetails?.connectors?.[selectedTab];
      targetConnectorId = currentConnector?.connector_id || currentConnector?.connectorId || currentConnector?.id;
    }
    
    // Store the connector ID
    setStopConnectorId(targetConnectorId);
    
    // Get transaction from that specific connector's transaction details section
    // Each connector has its own transaction stored in transactions[connectorId]
    const transactionToStop = targetConnectorId ? transactions[targetConnectorId] || null : null;
    
    // Get transaction ID from that specific connector's transaction details section
    // This transaction ID is from the transaction.transactionId field in that connector's transaction details
    const transactionId = transactionToStop?.transactionId || null;
    setStopTransactionId(transactionId);
    
    // Always open the dialog when button is clicked
    setStopDialogOpen(true);
    setStopError('');
    setUtrNumber('');
    setUtrNumberError('');
  };

  const handleStopConfirm = async () => {

    // Get transaction ID from the specific connector's transaction details section
    // This was collected when stop button was clicked from that connector's transaction details
    // Each connector has its own transaction, so we get the transaction ID from that specific connector
    let transactionId = stopTransactionId;
    
    // If stored transaction ID is not available, get it from the specific connector's transaction
    if (!transactionId && stopConnectorId) {
      const connectorTransaction = transactions[stopConnectorId];
      transactionId = connectorTransaction?.transactionId;
    }
    
    // Transaction ID should always be available from the connector's transaction details section
    // If not available, return early (transaction details may not be loaded for this connector)
    if (!transactionId) {
      return;
    }

    setUtrNumberError('');
    setStopping(true);
    setStopError('');

    try {
      // Get chargerId from URL
      const searchParams = new URLSearchParams(location.search);
      const urlChargerId = searchParams.get('chargerId');
      
      if (!urlChargerId) {
        setStopError('Charger ID is missing.');
        setStopping(false);
        return;
      }

      // Call stop transaction API with transactionId, chargerId, connectorId, and utrNumber
      const data = await stopTransaction(transactionId, urlChargerId.trim(), stopConnectorId, '');
      
      if (data && data.success) {
        // Refresh the transaction for the connector
        const searchParams = new URLSearchParams(location.search);
        const urlChargerId = searchParams.get('chargerId');
        const connectorIdToRefresh = stopConnectorId || (chargerDetails?.connectors?.[selectedTab]?.connector_id || chargerDetails?.connectors?.[selectedTab]?.connectorId || chargerDetails?.connectors?.[selectedTab]?.id);
        
        if (urlChargerId && connectorIdToRefresh) {
          try {
            const refreshData = await getActiveTransaction(urlChargerId.trim(), connectorIdToRefresh.trim());
            if (refreshData && refreshData.success && refreshData.data) {
              setTransactions(prev => ({ ...prev, [connectorIdToRefresh]: refreshData.data }));
            } else {
              setTransactions(prev => ({ ...prev, [connectorIdToRefresh]: null }));
            }
          } catch (err) {
            setTransactions(prev => ({ ...prev, [connectorIdToRefresh]: null }));
          }
        }
        setStopDialogOpen(false);
        setStopConnectorId(null);
        setStopTransactionId(null);
        setError('');
      } else {
        setStopError(data?.message || 'Failed to stop transaction');
      }
    } catch (err) {
      setStopError(err.message || 'Failed to stop transaction');
    } finally {
      setStopping(false);
    }
  };

  const handleStopCancel = () => {
    setStopDialogOpen(false);
    setStopError('');
    setUtrNumber('');
    setUtrNumberError('');
    setStopConnectorId(null);
    setStopTransactionId(null);
  };

  const handleUtrNumberChange = (event) => {
    const value = event.target.value.toUpperCase().replace(/[^A-Z0-9]/g, ''); // Only allow alphanumeric, convert to uppercase
    setUtrNumber(value);
    
    // Validate UTR number format on change
    if (value.trim() !== '') {
      const error = validateUtrNumber(value);
      setUtrNumberError(error);
    } else {
      setUtrNumberError('');
    }
  };

  // Notification handlers
  const handleNotificationClick = (connectorId, transactionId) => {
    setNotificationConnectorId(connectorId);
    setNotificationTransactionId(transactionId);
    setNotificationDialogOpen(true);
    setEmail('');
    setEmailError('');
    setOtpSent(false);
    setOtp('');
    setOtpError('');
    setNotificationError('');
    setNotificationSuccess(false);
  };

  const handleNotificationClose = () => {
    setNotificationDialogOpen(false);
    setNotificationConnectorId(null);
    setNotificationTransactionId(null);
    setEmail('');
    setEmailError('');
    setOtpSent(false);
    setOtp('');
    setOtpError('');
    setNotificationError('');
    setNotificationSuccess(false);
  };

  const validateEmail = (emailValue) => {
    // Email regex pattern
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    
    if (!emailValue || emailValue.trim() === '') {
      return 'Email is required';
    }
    
    if (!emailRegex.test(emailValue.trim())) {
      return 'Please enter a valid email address (e.g., user@example.com)';
    }
    
    // Check length
    if (emailValue.length > 100) {
      return 'Email address should be 100 characters or less';
    }
    
    return '';
  };

  const validateUtrNumber = (utrValue) => {
    // UTR (Unique Transaction Reference) number validation
    // UTR numbers are typically 12-16 alphanumeric characters
    // Can be digits only or alphanumeric
    const utrRegex = /^[A-Za-z0-9]{12,16}$/;
    
    if (!utrValue || utrValue.trim() === '') {
      return 'UTR number is required';
    }
    
    const trimmedUtr = utrValue.trim().toUpperCase();
    
    if (!utrRegex.test(trimmedUtr)) {
      return 'Please enter a valid UTR number (12-16 alphanumeric characters)';
    }
    
    return '';
  };

  const handleEmailChange = (event) => {
    const value = event.target.value;
    setEmail(value);
    if (emailError) {
      const error = validateEmail(value);
      setEmailError(error);
    }
  };

  const handleSendOTP = async () => {
      // Validate email
    const error = validateEmail(email);
      if (error) {
        setEmailError(error);
        return;
      }
      setEmailError('');
    const contactValue = email.trim();

    setSendingOtp(true);
    setNotificationError('');

    try {
      // Send OTP to email
      const data = await sendOTPToMobile(contactValue);
      
      if (data && data.success) {
        setOtpSent(true);
        setNotificationError('');
      } else {
        setNotificationError(data?.message || 'Failed to send OTP. Please try again.');
      }
    } catch (err) {
      setNotificationError(err.message || 'Failed to send OTP. Please try again.');
    } finally {
      setSendingOtp(false);
    }
  };

  const handleOTPChange = (event) => {
    const value = event.target.value.replace(/\D/g, ''); // Only allow digits
    if (value.length <= 6) {
      setOtp(value);
      if (otpError && value.length === 6) {
        setOtpError('');
      }
    }
  };

  const handleVerifyOTP = async () => {
    if (!otp || otp.length !== 6) {
      setOtpError('Please enter a valid 6-digit OTP');
      return;
    }

    setOtpError('');
    setVerifyingOtp(true);
    setNotificationError('');

    try {
      if (!notificationTransactionId) {
        setNotificationError('Transaction ID is missing.');
        setVerifyingOtp(false);
        return;
      }

      const contactValue = email.trim();
      
      const data = await verifyOTP(contactValue, otp, notificationTransactionId);
      
      if (data && data.success) {
        setNotificationSuccess(true);
        setNotificationError('');
        // Close dialog after 2 seconds
        setTimeout(() => {
          handleNotificationClose();
        }, 2000);
      } else {
        setNotificationError(data?.message || 'Invalid OTP. Please try again.');
      }
    } catch (err) {
      setNotificationError(err.message || 'Failed to verify OTP. Please try again.');
    } finally {
      setVerifyingOtp(false);
    }
  };

  // Helper function to render connector content (transaction or status)
  const renderConnectorContent = (connector, connectorId, isLoading) => {
    const transaction = transactions[connectorId];
    
    if (isLoading) {
      return (
        <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
          <CircularProgress size={40} />
        </Box>
      );
    }
    
    // Check if connector is in use
    const isInUse = connector.status?.toLowerCase() === 'in use' ||
      connector.connectorStatus?.toLowerCase() === 'in use' ||
      connector.status?.toLowerCase() === 'charging' ||
      connector.status?.toLowerCase() === 'preparing' ||
      connector.connectorStatus?.toLowerCase() === 'charging' ||
      connector.connectorStatus?.toLowerCase() === 'preparing';
    
    // If connector is in use, show transaction details (or loading state if transaction is being fetched)
    if (isInUse) {
      // If transaction is still loading, show loading state
      if (isLoading) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
            <CircularProgress size={40} />
          </Box>
        );
      }
      
      // If transaction exists, show transaction details
      if (transaction) {
        return (
          <>
            {/* Connector Information */}
            <DetailCard sx={{ mb: 2, boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}` }}>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
                  <Box
                    sx={{
                      width: { xs: 32, sm: 36 },
                      height: { xs: 32, sm: 36 },
                      borderRadius: 1.5,
                      background: alpha(theme.palette.primary.main, 0.1),
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                    }}
                  >
                    <Icon 
                      icon={flashFill} 
                      width={18} 
                      height={18} 
                      style={{ color: theme.palette.primary.main }} 
                    />
                  </Box>
                  <Typography 
                    variant="body1" 
                    sx={{ 
                      fontWeight: 600, 
                      color: 'text.primary',
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                    }}
                  >
                    Connector Information
                  </Typography>
                </Stack>
                <Divider sx={{ mb: 1.5, borderColor: alpha(theme.palette.primary.main, 0.1) }} />
                <Grid container spacing={{ xs: 1, sm: 1.25 }}>
                  {connector.connectorType && (
                    <Grid item xs={6} sm={6} md={4}>
                      <GridLabel>
                        <Typography className="label" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>Connector Type</Typography>
                        <Typography className="value" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {getConnectorTypeName(connector.connectorType)}
                        </Typography>
                      </GridLabel>
                    </Grid>
                  )}
                  {connector.kw && (
                    <Grid item xs={6} sm={6} md={4}>
                      <GridLabel>
                        <Typography className="label" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>Power</Typography>
                        <Typography className="value" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          {connector.kw} kW
                        </Typography>
                      </GridLabel>
                    </Grid>
                  )}
                  {connector.priceperKW && (
                    <Grid item xs={6} sm={6} md={4}>
                      <GridLabel>
                        <Typography className="label" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>Price</Typography>
                        <Typography className="value" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                          ₹{connector.priceperKW}/{connector.priceType || 'kWh'} + GST
                        </Typography>
                      </GridLabel>
                    </Grid>
                  )}
                </Grid>
              </CardContent>
            </DetailCard>

            {/* Transaction Details */}
            <DetailCard>
              <CardContent sx={{ p: { xs: 1.5, sm: 2 } }}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 1.5, sm: 2 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" sx={{ mb: { xs: 1.5, sm: 2 } }}>
                  <Stack direction="row" spacing={1} alignItems="center" sx={{ flex: 1 }}>
                    <Box
                      sx={{
                        width: { xs: 32, sm: 36 },
                        height: { xs: 32, sm: 36 },
                        borderRadius: 1.5,
                        background: alpha(theme.palette.primary.main, 0.1),
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                      }}
                    >
                      <Icon 
                        icon={fileTextFill} 
                        width={18} 
                        height={18} 
                        style={{ color: theme.palette.primary.main }} 
                      />
                    </Box>
                    <Typography 
                      variant="body1" 
                      sx={{ 
                        fontWeight: 600, 
                        color: 'text.primary',
                        fontSize: { xs: '0.875rem', sm: '1rem' },
                      }}
                    >
                      Transaction Details
                    </Typography>
                  </Stack>
                <Box sx={{ width: { xs: '100%', sm: 'auto' }, display: 'flex', gap: 1, flexDirection: { xs: 'column', sm: 'row' }, justifyContent: { xs: 'center', sm: 'flex-end' } }}>
                  {transaction.isActive && (
                  <Button
                    variant="outlined"
                    color="primary"
                    size="medium"
                      onClick={() => handleNotificationClick(connectorId, transaction.transactionId)}
                    startIcon={<Icon icon={bellFill} width={18} height={18} />}
                    sx={{
                      width: { xs: '100%', sm: 'auto' },
                      fontWeight: 700,
                      fontSize: { xs: '0.75rem', sm: '0.875rem' },
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.25 },
                      borderRadius: 2,
                      minWidth: { xs: 'auto', sm: 200 },
                      border: '2px solid',
                      '&:hover': {
                        border: '2px solid',
                        transform: { xs: 'none', sm: 'translateY(-2px)' },
                      },
                    }}
                  >
                    Get Charging Updates
                  </Button>
                  )}
                  {transaction.isActive ? (
                    <Button
                      variant="contained"
                      color="error"
                      size="medium"
                      onClick={() => handleStopClick(connectorId)}
                      disabled={stopping}
                      startIcon={<Icon icon={stopCircleFill} width={18} height={18} />}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        fontWeight: 700,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1, sm: 1.25 },
                        borderRadius: 2,
                        minWidth: { xs: 'auto', sm: 180 },
                        boxShadow: '0 4px 16px rgba(211, 47, 47, 0.4)',
                        backgroundColor: '#d32f2f',
                        border: '1px solid #b71c1c',
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(211, 47, 47, 0.5)',
                          transform: { xs: 'none', sm: 'translateY(-2px) scale(1.02)' },
                          backgroundColor: '#b71c1c',
                          border: '2px solid #8b0000',
                        },
                        '&:active': {
                          transform: 'translateY(0) scale(0.98)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                        '&:disabled': {
                          backgroundColor: '#ffcdd2',
                          color: '#ffffff',
                          border: '2px solid #ffcdd2',
                        },
                      }}
                    >
                      Stop Transaction
                    </Button>
                  ) : (
                    <Button
                      variant="contained"
                      color="primary"
                      size="medium"
                      onClick={handleDownloadInvoice}
                      startIcon={<Icon icon={downloadFill} width={18} height={18} />}
                      sx={{
                        width: { xs: '100%', sm: 'auto' },
                        fontWeight: 700,
                        fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        px: { xs: 2, sm: 3 },
                        py: { xs: 1, sm: 1.25 },
                        borderRadius: 2,
                        minWidth: { xs: 'auto', sm: 180 },
                        boxShadow: '0 4px 16px rgba(25, 118, 210, 0.4)',
                        backgroundColor: '#1976d2',
                        border: '1px solid #1565c0',
                        '&:hover': {
                          boxShadow: '0 6px 20px rgba(25, 118, 210, 0.5)',
                          transform: { xs: 'none', sm: 'translateY(-2px) scale(1.02)' },
                          backgroundColor: '#1565c0',
                          border: '2px solid #0d47a1',
                        },
                        '&:active': {
                          transform: 'translateY(0) scale(0.98)',
                        },
                        transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
                      }}
                    >
                      Download Invoice
                    </Button>
                  )}
                </Box>
              </Stack>
              <Divider sx={{ mb: { xs: 1, sm: 1.5 }, borderColor: alpha(theme.palette.primary.main, 0.1) }} />

              <Grid container spacing={{ xs: 1, sm: 1.25 }}>
                <Grid item xs={6} sm={6} md={4}>
                  <GridLabel>
                    <Typography className="label" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>Transaction ID</Typography>
                    <Typography className="value" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{formatValue(transaction.transactionId)}</Typography>
                  </GridLabel>
                </Grid>
                <Grid item xs={6} sm={6} md={4}>
                  <GridLabel>
                    <Typography className="label" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>Start Timestamp</Typography>
                    <Typography className="value" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                      {transaction.startTimestamp ? fDateTime(transaction.startTimestamp) : '—'}
                    </Typography>
                  </GridLabel>
                </Grid>
                <Grid item xs={6} sm={6} md={4}>
                  <GridLabel>
                    <Typography className="label" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>kW Consumed</Typography>
                    <Typography className="value" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{formatValue(transaction.kwConsumed)}</Typography>
                  </GridLabel>
                </Grid>
                {(transaction.amountPaid !== undefined && transaction.amountPaid !== null) && (
                  <Grid item xs={6} sm={6} md={4}>
                    <GridLabel>
                      <Typography className="label" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>Amount Paid</Typography>
                      <Typography className="value" sx={{ fontWeight: 600, color: 'success.main', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        ₹{formatValue(transaction.amountPaid)}
                      </Typography>
                    </GridLabel>
                  </Grid>
                )}
                {calculateAmountUsed(transaction) !== null && (
                  <Grid item xs={6} sm={6} md={4}>
                    <GridLabel>
                      <Typography className="label" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>Amount Used (Incl. GST)</Typography>
                      <Typography className="value" sx={{ fontWeight: 600, color: 'warning.main', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        ₹{calculateAmountUsed(transaction)}
                      </Typography>
                    </GridLabel>
                  </Grid>
                )}
                {calculateAmountRemaining(transaction) !== null && (
                  <Grid item xs={6} sm={6} md={4}>
                    <GridLabel>
                      <Typography className="label" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>Amount Remaining</Typography>
                      <Typography className="value" sx={{ fontWeight: 600, color: calculateAmountRemaining(transaction) >= 0 ? 'info.main' : 'error.main', fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                        ₹{calculateAmountRemaining(transaction)}
                      </Typography>
                    </GridLabel>
                  </Grid>
                )}
                <Grid item xs={6} sm={6} md={4}>
                  <GridLabel>
                    <Typography className="label" sx={{ fontSize: { xs: '0.65rem', sm: '0.7rem' } }}>Battery Percent</Typography>
                    <Typography className="value" sx={{ fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>{formatValue(transaction.batteryPercent)}%</Typography>
                  </GridLabel>
                </Grid>
              </Grid>
            </CardContent>
          </DetailCard>
          </>
      );
      }
      
      // If connector is in use but no transaction found, show loading or message
      // The useEffect will handle fetching the transaction
      if (transactionLoading[connectorId]) {
        return (
          <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', py: 3 }}>
            <CircularProgress size={40} />
          </Box>
        );
      }
      
      // If still no transaction after API call, show message
      return (
        <DetailCard sx={{ 
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          width: '100%',
          maxWidth: '100%',
        }}>
          <CardContent sx={{ 
            p: { xs: 1.5, sm: 2 },
            width: '100%',
            '&:last-child': { pb: { xs: 1.5, sm: 2 } }
          }}>
            <Alert severity="info" sx={{ borderRadius: 2, mb: 2 }}>
              <Typography variant="body2" sx={{ fontWeight: 500 }}>
                Connector {connectorId} is in use, but no active transaction was found. The transaction may have been completed or is being processed.
              </Typography>
            </Alert>
          </CardContent>
        </DetailCard>
      );
    } else {
      // Show Connector Status (when connector is not in use)
      return (
        <DetailCard sx={{ 
          boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}`,
          width: '100%',
          maxWidth: '100%',
        }}>
          <CardContent sx={{ 
            p: { xs: 1.5, sm: 2 },
            width: '100%',
            '&:last-child': { pb: { xs: 1.5, sm: 2 } }
          }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Box
                sx={{
                  width: { xs: 36, sm: 40 },
                  height: { xs: 36, sm: 40 },
                  borderRadius: 1.5,
                  background: alpha(theme.palette.primary.main, 0.15),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.2)}`,
                }}
              >
                <Icon 
                  icon={flashFill} 
                  width={20} 
                  height={20} 
                  style={{ color: theme.palette.primary.main }} 
                />
              </Box>
              <Box sx={{ flex: 1, textAlign: 'left' }}>
                <Typography 
                  variant="body1" 
                  sx={{ 
                    fontWeight: 600, 
                    color: 'text.primary',
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    textAlign: 'left',
                  }}
                >
                  Connector {connectorId} Status
                </Typography>
                <Typography 
                  variant="caption" 
                  sx={{ 
                    color: 'text.secondary',
                    fontSize: { xs: '0.65rem', sm: '0.7rem' },
                  }}
                >
                  Current status and details
                </Typography>
              </Box>
            </Stack>
            
            <Box
              sx={{
                width: '100%',
                p: { xs: 1.5, sm: 2 },
                borderRadius: 2,
                background: alpha(theme.palette.primary.main, 0.06),
                border: `2px solid ${alpha(theme.palette.primary.main, 0.2)}`,
                transition: 'all 0.3s ease',
                '&:hover': {
                  transform: 'translateY(-2px)',
                  boxShadow: `0 8px 24px ${alpha(theme.palette.primary.main, 0.2)}`,
                  borderColor: alpha(theme.palette.primary.main, 0.4),
                },
              }}
            >
              <Stack direction="column" spacing={1.5}>
                <Stack direction={{ xs: 'column', sm: 'row' }} spacing={{ xs: 0.75, sm: 1 }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between">
                  <Box sx={{ flex: 1, textAlign: 'left' }}>
                    <Typography 
                      variant="caption" 
                      sx={{ 
                        color: 'text.secondary', 
                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                        fontWeight: 600,
                        textTransform: 'uppercase',
                        letterSpacing: '1px',
                        display: 'block',
                      }}
                    >
                      Connector {connectorId}
                    </Typography>
                  </Box>
                  <Stack direction="row" spacing={1} alignItems="center">
                    <Chip
                      label={
                        (chargerDetails.status?.toString().toUpperCase() === 'N/A' || chargerDetails.status?.toString().toUpperCase() === 'NA')
                          ? 'N/A'
                          : chargerDetails.status !== 'A' && chargerDetails.status?.toLowerCase() !== 'active'
                          ? 'Inactive'
                          : connector.status || connector.connectorStatus || 'Unknown'
                      }
                      size="small"
                      color={
                        (chargerDetails.status?.toString().toUpperCase() === 'N/A' || chargerDetails.status?.toString().toUpperCase() === 'NA')
                          ? 'default'
                          : chargerDetails.status !== 'A' && chargerDetails.status?.toLowerCase() !== 'active'
                          ? 'error'
                          : connector.status?.toLowerCase() === 'available' ||
                            connector.connectorStatus?.toLowerCase() === 'available'
                          ? 'success'
                          : connector.status?.toLowerCase() === 'in use' ||
                            connector.connectorStatus?.toLowerCase() === 'in use' ||
                            connector.status?.toLowerCase() === 'charging' ||
                            connector.status?.toLowerCase() === 'preparing' ||
                            connector.connectorStatus?.toLowerCase() === 'charging' ||
                            connector.connectorStatus?.toLowerCase() === 'preparing'
                          ? 'warning'
                          : connector.status?.toLowerCase() === 'unavailable' ||
                            connector.status?.toLowerCase() === 'not available' ||
                            connector.status?.toLowerCase() === 'faulted' ||
                            connector.connectorStatus?.toLowerCase() === 'unavailable' ||
                            connector.connectorStatus?.toLowerCase() === 'not available' ||
                            connector.connectorStatus?.toLowerCase() === 'faulted'
                          ? 'error'
                          : 'default'
                      }
                      sx={{
                        fontWeight: 600,
                        fontSize: { xs: '0.65rem', sm: '0.7rem' },
                        height: { xs: 22, sm: 24 },
                        minWidth: { xs: 80, sm: 90 },
                      }}
                    />
                    {isInUse ? (
                      <Button
                        variant="contained"
                        color="error"
                        size="small"
                        onClick={() => handleStopClick(connectorId)}
                        disabled={stopping}
                        startIcon={<Icon icon={stopCircleFill} width={16} height={16} />}
                        sx={{
                          fontWeight: 700,
                          fontSize: { xs: '0.65rem', sm: '0.7rem' },
                          px: { xs: 1, sm: 1.5 },
                          py: { xs: 0.5, sm: 0.75 },
                          borderRadius: 1.5,
                          minWidth: 'auto',
                          height: { xs: 22, sm: 24 },
                          boxShadow: '0 2px 8px rgba(211, 47, 47, 0.3)',
                          backgroundColor: '#d32f2f',
                          border: '1px solid #b71c1c',
                          '&:hover': {
                            boxShadow: '0 4px 12px rgba(211, 47, 47, 0.4)',
                            backgroundColor: '#b71c1c',
                          },
                          '&:disabled': {
                            backgroundColor: '#ffcdd2',
                            color: '#ffffff',
                          },
                        }}
                      >
                        Stop Transaction
                      </Button>
                    ) : null}
                  </Stack>
                </Stack>
                <Divider sx={{ borderColor: alpha(theme.palette.primary.main, 0.15) }} />
                <Grid container spacing={{ xs: 1, sm: 1.5 }} sx={{ width: '100%' }}>
                  {connector.connectorType && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        sx={{
                          p: { xs: 1, sm: 1.25 },
                          borderRadius: 2,
                          background: theme.palette.background.paper,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          height: '100%',
                          minHeight: { xs: 80, sm: 90 },
                          width: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                          },
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: { xs: '0.6rem', sm: '0.65rem' },
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          Type
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.primary',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            fontWeight: 600,
                          }}
                        >
                          {getConnectorTypeName(connector.connectorType)}
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {connector.kw && (
                    <Grid item xs={12} sm={6} md={4}>
                      <Box
                        sx={{
                          p: { xs: 1, sm: 1.25 },
                          borderRadius: 2,
                          background: theme.palette.background.paper,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          height: '100%',
                          minHeight: { xs: 80, sm: 90 },
                          width: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                          },
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: { xs: '0.6rem', sm: '0.65rem' },
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          Power
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.primary',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            fontWeight: 600,
                          }}
                        >
                          {connector.kw} kW
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                  {connector.priceperKW && (
                    <Grid item xs={12} sm={12} md={4}>
                      <Box
                        sx={{
                          p: { xs: 1, sm: 1.25 },
                          borderRadius: 2,
                          background: theme.palette.background.paper,
                          border: `1px solid ${alpha(theme.palette.primary.main, 0.12)}`,
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          justifyContent: 'center',
                          textAlign: 'center',
                          height: '100%',
                          minHeight: { xs: 80, sm: 90 },
                          width: '100%',
                          transition: 'all 0.3s ease',
                          '&:hover': {
                            transform: 'translateY(-2px)',
                            boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                            borderColor: alpha(theme.palette.primary.main, 0.3),
                          },
                        }}
                      >
                        <Typography 
                          variant="caption" 
                          sx={{ 
                            color: 'text.secondary',
                            fontSize: { xs: '0.6rem', sm: '0.65rem' },
                            fontWeight: 600,
                            textTransform: 'uppercase',
                            letterSpacing: '0.5px',
                            display: 'block',
                            mb: 0.5,
                          }}
                        >
                          Price
                        </Typography>
                        <Typography 
                          variant="body2" 
                          sx={{ 
                            color: 'text.primary',
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                            fontWeight: 600,
                          }}
                        >
                          ₹{connector.priceperKW}/{connector.priceType || 'kWh'} + GST
                        </Typography>
                      </Box>
                    </Grid>
                  )}
                </Grid>
              </Stack>
            </Box>
          </CardContent>
        </DetailCard>
      );
    }
  };

  const formatValue = (value) => {
    if (value === null || value === undefined) {
      return '—';
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  };

  // Calculate price per kW with GST (18%)
  const getPricePerKwWithGST = (transaction) => {
    if (!transaction) return null;
    const pricePerKw = parseFloat(transaction.pricePerKw || 0);
    if (pricePerKw > 0) {
      return (pricePerKw).toFixed(2);
    }
    return null;
  };

  // Calculate base amount (before GST)
  const calculateBaseAmount = (transaction) => {
    if (!transaction) return null;
    const pricePerKw = parseFloat(transaction.pricePerKw || 0);
    const kwConsumed = parseFloat(transaction.kwConsumed || 0);
    if (pricePerKw > 0 && kwConsumed > 0) {
      return (pricePerKw * kwConsumed).toFixed(2);
    }
    return null;
  };

  // Calculate GST amount (18%)
  const calculateGSTAmount = (transaction) => {
    const baseAmount = calculateBaseAmount(transaction);
    if (baseAmount !== null) {
      return (parseFloat(baseAmount) * 0.18).toFixed(2);
    }
    return null;
  };

  // Calculate total amount used with GST (18%)
  const calculateAmountUsed = (transaction) => {
    if (!transaction) return null;
    const pricePerKw = parseFloat(transaction.pricePerKw || 0);
    const kwConsumed = parseFloat(transaction.kwConsumed || 0);
    if (pricePerKw > 0 && kwConsumed > 0) {
      // Calculate: (pricePerKw * 1.18) * kwConsumed (price with GST * consumption)
      return (pricePerKw * 1.18 * kwConsumed).toFixed(2);
    }
    return null;
  };

  const calculateAmountRemaining = (transaction) => {
    if (!transaction) return null;
    const amountPaid = parseFloat(transaction.amountPaid || 0);
    const amountUsed = calculateAmountUsed(transaction);
    if (amountPaid > 0 && amountUsed !== null) {
      const remaining = amountPaid - parseFloat(amountUsed);
      return remaining.toFixed(2);
    }
    return null;
  };

  // Generate and download invoice PDF
  const handleDownloadInvoice = () => {
    const transaction = getCurrentTransaction();
    if (!transaction) return;

    const startTime = transaction.startTimestamp ? fDateTime(transaction.startTimestamp) : 'N/A';
    const stopTime = transaction.stopTimestamp ? fDateTime(transaction.stopTimestamp) : 'N/A';
    const kwConsumed = parseFloat(transaction.kwConsumed || 0);
    const baseAmount = calculateBaseAmount(transaction);
    const gstAmount = calculateGSTAmount(transaction);
    const totalAmount = calculateAmountUsed(transaction);
    const amountPaid = parseFloat(transaction.amountPaid || 0);
    const amountRemaining = calculateAmountRemaining(transaction);

    // Construct full logo URL from domain config
    const domainLogoUrl = domainConfig?.logoUrl || logoUrl;
    const fullLogoUrl = domainLogoUrl.startsWith('http') 
      ? domainLogoUrl 
      : `${window.location.origin}${domainLogoUrl.startsWith('/') ? domainLogoUrl : '/' + domainLogoUrl}`;
    
    // Fallback logo URL
    const fallbackLogoUrl = `${window.location.origin}/logo/amikCharge.svg`;

    const invoiceHTML = `
      <!DOCTYPE html>
      <html>
      <head>
        <meta name="viewport" content="width=device-width, initial-scale=1.0">
        <title>Invoice - ${transaction.transactionId}</title>
        <style>
          * {
            margin: 0;
            padding: 0;
            box-sizing: border-box;
          }
          body {
            font-family: Arial, sans-serif;
            font-size: 12px;
            line-height: 1.4;
            color: #333;
            background: white;
            padding: 8px;
            margin: 0;
            width: 210mm;
            min-height: 297mm;
          }
          .invoice-container {
            width: 210mm;
            min-height: 297mm;
            margin: 0 auto;
            background: white;
            padding: 10px;
            border-radius: 8px;
            box-shadow: 0 2px 10px rgba(0,0,0,0.1);
            overflow: visible;
          }
          .header {
            display: flex;
            justify-content: space-between;
            align-items: flex-start;
            margin-bottom: 15px;
            padding: 15px;
            background: linear-gradient(135deg, #1A73E8 0%, #34A853 50%, #0D47A1 100%);
            border-radius: 8px;
            color: white;
            position: relative;
            overflow: hidden;
          }
          .header::before {
            content: '';
            position: absolute;
            top: 0;
            left: 0;
            right: 0;
            bottom: 0;
            background: linear-gradient(45deg, transparent 30%, rgba(255, 255, 255, 0.15) 50%, transparent 70%);
            pointer-events: none;
          }
          .header-left {
            display: flex;
            align-items: center;
            gap: 12px;
            flex: 1;
            min-width: 0;
            position: relative;
            z-index: 1;
          }
          .logo {
            width: 100px;
            height: 100px;
            display: flex;
            align-items: center;
            justify-content: center;
            background: rgba(255, 255, 255, 0.95);
            padding: 8px;
            border-radius: 8px;
            box-shadow: 0 2px 8px rgba(0, 0, 0, 0.15);
            flex-shrink: 0;
          }
          .logo img {
            max-width: 100%;
            max-height: 100%;
            width: auto;
            height: auto;
            object-fit: contain;
            display: block;
          }
          .header-title-section {
            flex: 1;
            min-width: 0;
          }
          .header-title-row {
            display: flex;
            align-items: center;
            gap: 6px;
            margin-bottom: 6px;
          }
          .header-icon {
            width: 32px;
            height: 32px;
            border-radius: 50%;
            background: linear-gradient(135deg, #1976d2 0%, #1565c0 100%);
            display: flex;
            align-items: center;
            justify-content: center;
            flex-shrink: 0;
          }
          .header-icon svg {
            width: 16px;
            height: 16px;
            color: white;
          }
          .header h1 {
            font-size: 20px;
            color: white;
            margin: 0;
            font-weight: 700;
            letter-spacing: -0.3px;
            text-shadow: 0 2px 4px rgba(0,0,0,0.2);
            line-height: 1.2;
          }
          .header-info {
            display: flex;
            flex-direction: column;
            gap: 2px;
            margin-left: 38px;
            font-size: 11px;
          }
          .header-info-item {
            color: rgba(255, 255, 255, 0.95);
            font-weight: 500;
            line-height: 1.4;
          }
          .header-info-item strong {
            color: white;
            font-weight: 700;
          }
          .header-right {
            text-align: right;
            display: flex;
            flex-direction: column;
            justify-content: flex-start;
            position: relative;
            z-index: 1;
          }
          .company-name {
            font-size: 14px;
            font-weight: 700;
            color: white;
            margin-bottom: 4px;
            text-shadow: 0 1px 2px rgba(0,0,0,0.2);
          }
          .company-email {
            font-size: 11px;
            color: rgba(255,255,255,0.9);
            margin-bottom: 4px;
          }
          .invoice-number-header {
            font-size: 10px;
            color: rgba(255,255,255,0.8);
            padding-top: 6px;
            border-top: 1px solid rgba(255,255,255,0.3);
          }
          .section {
            margin-bottom: 12px;
            padding: 10px;
            background: #f8f9fa;
            border-radius: 6px;
          }
          .section-title {
            font-size: 14px;
            font-weight: bold;
            color: #1976d2;
            margin-bottom: 8px;
            padding-bottom: 6px;
            border-bottom: 1px solid #ddd;
          }
          .info-row {
            display: flex;
            justify-content: space-between;
            padding: 5px 0;
            border-bottom: 1px solid #e0e0e0;
            font-size: 12px;
          }
          .info-row:last-child {
            border-bottom: none;
          }
          .info-label {
            font-weight: 600;
            color: #666;
          }
          .info-value {
            color: #333;
            text-align: right;
          }
          .amount-section {
            background: #e3f2fd;
            padding: 10px;
            border-radius: 6px;
            margin-top: 12px;
          }
          .amount-row {
            display: flex;
            justify-content: space-between;
            padding: 6px 0;
            font-size: 13px;
          }
          .amount-row.total {
            font-size: 16px;
            font-weight: bold;
            color: #1976d2;
            border-top: 2px solid #1976d2;
            padding-top: 8px;
            margin-top: 6px;
          }
          .footer {
            text-align: center;
            margin-top: 12px;
            padding-top: 10px;
            border-top: 1px solid #ddd;
            font-size: 11px;
            color: #666;
          }
          @media print {
            body {
              background: white;
              padding: 0;
            }
            .invoice-container {
              box-shadow: none;
              border-radius: 0;
            }
          }
          @media (max-width: 600px) {
            body {
              padding: 5px;
            }
            .invoice-container {
              padding: 15px;
            }
            .header {
              flex-direction: column;
              align-items: flex-start;
              padding: 15px;
            }
            .header-left {
              width: 100%;
              margin-bottom: 15px;
            }
            .header-right {
              text-align: left;
              width: 100%;
            }
            .header h1 {
              font-size: 20px;
            }
            .logo {
              width: 80px;
              height: 80px;
              padding: 6px;
            }
            .header-info {
              margin-left: 0;
              margin-top: 8px;
            }
            .section {
              padding: 12px;
            }
          }
        </style>
      </head>
      <body>
        <div class="invoice-container">
          <div class="header">
            <div class="header-left">
              <div class="logo">
                <img src="${fullLogoUrl}" alt="Company Logo" style="display: block; width: 100%; height: 40%; object-fit: contain;" onerror="this.onerror=null; this.src='${fallbackLogoUrl}';" />
              </div>
              <div class="header-title-section" align="right">
                <div class="header-title-row" align="right">
                  
                  <h1> Invoice #: ${transaction.transactionId}</h1>
                </div>
              
              </div>
            </div>
           
          </div>

          <div class="section">
            <div class="section-title">Transaction Details</div>
            <div class="info-row">
              <span class="info-label">Invoice #</span>
              <span class="info-value">${transaction.transactionId}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Date</span>
              <span class="info-value">${startTime}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Status</span>
              <span class="info-value">${transaction.isActive ? 'Active' : 'Completed'}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Charger</span>
              <span class="info-value">${transaction.chargerName || 'N/A'}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Connector ID</span>
              <span class="info-value">${transaction.connectorId || 'N/A'}</span>
            </div>
          </div>

          <div class="section">
            <div class="section-title">Charging Session</div>
            <div class="info-row">
              <span class="info-label">Start Time</span>
              <span class="info-value">${startTime}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Stop Time</span>
              <span class="info-value">${stopTime}</span>
            </div>
            <div class="info-row">
              <span class="info-label">Energy Consumed</span>
              <span class="info-value">${kwConsumed.toFixed(3)} kWh</span>
            </div>
            ${transaction.pricePerKw ? `
            <div class="info-row">
              <span class="info-label">Price Per kW (Excluding GST)</span>
              <span class="info-value">₹${transaction.pricePerKw}</span>
            </div>
            ` : ''}
          </div>

          <div class="amount-section">
            <div class="section-title" style="background: transparent; padding: 0; margin-bottom: 10px; border: none;">Amount Breakdown</div>
            ${baseAmount ? `
            <div class="amount-row">
              <span>Base Amount</span>
              <span>₹${baseAmount}</span>
            </div>
            ` : ''}
            ${gstAmount ? `
            <div class="amount-row">
              <span>GST (18%)</span>
              <span>₹${gstAmount}</span>
            </div>
            ` : ''}
            <div class="amount-row total">
              <span>Total (Incl. GST)</span>
              <span>₹${totalAmount || '0.00'}</span>
            </div>
            ${amountPaid > 0 ? `
            <div class="amount-row" style="margin-top: 10px; padding-top: 10px; border-top: 1px solid #ddd;">
              <span>Amount Paid</span>
              <span>₹${amountPaid.toFixed(2)}</span>
            </div>
            ` : ''}
            ${amountRemaining !== null ? `
            <div class="amount-row">
              <span>Amount Refunded</span>
              <span>₹${amountRemaining}</span>
            </div>
            ` : ''}
          </div>

          <div class="footer">
            <p><strong>Thank you for using ${domainConfig?.name || 'Charging'} Services!</strong></p>
            ${domainConfig?.email ? `<p>For support: ${domainConfig.email}</p>` : ''}
          </div>
        </div>
      </body>
      </html>
    `;

    // Create a temporary div to render the invoice (better for mobile)
    const tempDiv = document.createElement('div');
    tempDiv.style.position = 'absolute';
    tempDiv.style.left = '-9999px';
    tempDiv.style.top = '0';
    tempDiv.style.width = '210mm';
    tempDiv.style.minHeight = '297mm';
    tempDiv.style.background = 'white';
    tempDiv.innerHTML = invoiceHTML;
    document.body.appendChild(tempDiv);
    
    // Wait for content and images to load
    const generatePDF = () => {
      try {
        // Load html2canvas dynamically if not available
        const loadHtml2Canvas = () => {
          return new Promise((resolve, reject) => {
            if (window.html2canvas) {
              resolve(window.html2canvas);
              return;
            }
            
            const script = document.createElement('script');
            script.src = 'https://cdnjs.cloudflare.com/ajax/libs/html2canvas/1.4.1/html2canvas.min.js';
            script.onload = () => resolve(window.html2canvas);
            script.onerror = () => reject(new Error('Failed to load html2canvas'));
            document.head.appendChild(script);
          });
        };
        
        loadHtml2Canvas().then((html2canvas) => {
          // Use the container div instead of body for better mobile compatibility
          const container = tempDiv.querySelector('.invoice-container') || tempDiv;
          return html2canvas(container, {
            scale: 2,
            useCORS: true,
            logging: false,
            allowTaint: true,
            backgroundColor: '#ffffff',
            width: container.scrollWidth || 794, // A4 width in pixels at 96dpi
            height: container.scrollHeight || 1123, // A4 height in pixels
            windowWidth: container.scrollWidth || 794,
            windowHeight: container.scrollHeight || 1123,
          });
        }).then((canvas) => {
          const imgData = canvas.toDataURL('image/png', 1.0);
          const pdf = new jsPDF('p', 'mm', 'a4');
          const imgWidth = 210; // A4 width in mm
          const pageHeight = 297; // A4 height in mm
          const imgHeight = (canvas.height * imgWidth) / canvas.width;
          let heightLeft = imgHeight;
          let position = 0;
          
          pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
          heightLeft -= pageHeight;
          
          while (heightLeft > 0) {
            position = heightLeft - imgHeight;
            pdf.addPage();
            pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
            heightLeft -= pageHeight;
          }
          
          pdf.save(`Invoice_${transaction.transactionId}.pdf`);
          document.body.removeChild(tempDiv);
        }).catch((error) => {
          fallbackPrint();
        });
      } catch (error) {
        fallbackPrint();
      }
    };
    
    const fallbackPrint = () => {
      const printWindow = window.open('', '_blank');
      printWindow.document.write(invoiceHTML);
      printWindow.document.close();
      printWindow.onload = () => {
        printWindow.focus();
        printWindow.print();
      };
      document.body.removeChild(tempDiv);
    };
    
    // Wait for images to load before generating PDF
    setTimeout(() => {
      const images = tempDiv.querySelectorAll('img');
      let imagesLoaded = 0;
      const totalImages = images.length;
      
      const checkAndGenerate = () => {
        if (imagesLoaded === totalImages || totalImages === 0) {
          setTimeout(generatePDF, 800); // Longer delay for mobile rendering
        }
      };
      
      if (totalImages > 0) {
        images.forEach((img) => {
          if (img.complete && img.naturalHeight !== 0) {
            imagesLoaded++;
            checkAndGenerate();
          } else {
            img.onload = () => {
              imagesLoaded++;
              checkAndGenerate();
            };
            img.onerror = () => {
              imagesLoaded++;
              checkAndGenerate();
            };
            // Timeout for images that never load
            setTimeout(() => {
              if (!img.complete) {
                imagesLoaded++;
                checkAndGenerate();
              }
            }, 3000);
          }
        });
      } else {
        setTimeout(generatePDF, 800);
      }
    }, 300); // Initial delay to ensure DOM is ready
  };

  return (
    <RootStyle title="Active Transaction">
      <Container 
        maxWidth="lg" 
        sx={{ 
          position: 'relative', 
          zIndex: 1,
          py: { xs: 1, sm: 2, md: 3 },
          px: { xs: 1, sm: 2, md: 3 }
        }}
      >

        {loading && (
          <Box 
            sx={{ 
              display: 'flex', 
              flexDirection: 'column',
              justifyContent: 'center', 
              alignItems: 'center', 
              minHeight: '400px',
              position: 'relative',
              zIndex: 1,
            }}
          >
            <Box sx={{ display: { xs: 'block', sm: 'none' } }}>
              <CircularProgress 
                size={60}
                thickness={4}
                sx={{
                  color: theme.palette.primary.main,
                  animationDuration: '1.5s',
                }}
              />
            </Box>
            <Box sx={{ display: { xs: 'none', sm: 'block' } }}>
            <CircularProgress 
              size={80} 
              thickness={4}
              sx={{
                color: theme.palette.primary.main,
                animationDuration: '1.5s',
              }}
            />
            </Box>
            <Typography 
              variant="h6" 
              sx={{ 
                mt: 2,
                color: 'text.secondary',
                fontWeight: 500,
                letterSpacing: '0.5px',
                fontSize: { xs: '0.875rem', sm: '1rem' },
                textAlign: 'center',
                px: 2,
              }}
            >
              Loading transaction details...
            </Typography>
          </Box>
        )}

        {!loading && hasSearched && (error || chargerDetails || chargerLoading) && (
          <SearchCard>
            <CardContent sx={{ p: { xs: 2, sm: 3, md: 4 }, textAlign: 'center', position: 'relative' }}>
              <Box
                sx={{
                  position: 'absolute',
                  top: { xs: 8, sm: 16 },
                  left: { xs: 8, sm: 16 },
                  zIndex: 10,
                  backgroundColor: 'transparent',
                }}
              >
                <img
                  src={logoUrl}
                  alt="Logo"
                  onError={(e) => {
                    e.target.onerror = null;
                    e.target.src = '/logo/amikCharge.svg';
                  }}
                  style={{
                    width: 'auto',
                    maxWidth: '120px',
                    maxHeight: '25px',
                    height: 'auto',
                    display: 'block',
                    backgroundColor: 'transparent',
                    filter: 'drop-shadow(0 2px 4px rgba(0, 0, 0, 0.08))',
                  }}
                />
              </Box>
              
              {/* Error Message */}
              {error && !chargerLoading && !chargerDetails && (
                <Box sx={{ mt: 3, mb: 2 }}>
                  <Alert severity="error" sx={{ borderRadius: 2 }}>
                    {error}
                  </Alert>
                </Box>
              )}
              
              {/* Charger Details Section */}
              {chargerLoading && (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', mt: 3, mb: 2 }}>
                  <CircularProgress size={{ xs: 40, sm: 50 }} />
                </Box>
              )}
              
              {chargerDetails && !chargerLoading && (
                <Box sx={{ mt: 3 }}>
                  {/* Header */}
                  <Box sx={{ mb: 3 }}>
                    <Divider 
                      sx={{ 
                        borderColor: alpha(theme.palette.primary.main, 0.2),
                        borderWidth: 1,
                      }} 
                    />
                  </Box>

                  {/* Charger Information Card */}
                  <DetailCard sx={{ mb: 3, boxShadow: `0 8px 32px ${alpha(theme.palette.primary.main, 0.12)}` }}>
                    <CardContent sx={{ p: { xs: 2.5, sm: 3, md: 3.5 } }}>
                      <Stack direction="row" spacing={1} alignItems="center" sx={{ mb: 1.5 }}>
              <Box
                sx={{
                            width: { xs: 32, sm: 36 },
                            height: { xs: 32, sm: 36 },
                            borderRadius: 1.5,
                            background: alpha(theme.palette.primary.main, 0.1),
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center',
                          }}
                        >
                          <Icon 
                            icon={batteryFill} 
                            width={18} 
                            height={18} 
                            style={{ color: theme.palette.primary.main }} 
                          />
              </Box>
                        <Box sx={{ flex: 1, textAlign: 'left' }}>
                          <Typography 
                            variant="body1" 
                            sx={{ 
                              fontWeight: 600, 
                              color: 'text.primary',
                              fontSize: { xs: '0.875rem', sm: '1rem' },
                              textAlign: 'left',
                            }}
                          >
                            Charger Address
              </Typography>
                          {chargerDetails.street && (
                            <Typography 
                              variant="caption" 
                  sx={{ 
                                color: 'text.secondary',
                                fontSize: { xs: '0.65rem', sm: '0.7rem' },
                              }}
                            >
                              {chargerDetails.street}
                            </Typography>
                          )}
                        </Box>
                      </Stack>
                      
                      <Grid container spacing={{ xs: 1.5, sm: 2.5 }}>
                        {(chargerDetails.chargepoint_name || chargerDetails.name) && (
                          <Grid item xs={6} sm={6}>
                            <Box
                              sx={{
                                p: { xs: 1.5, sm: 2 },
                  borderRadius: 2,
                                background: alpha(theme.palette.primary.main, 0.05),
                                border: `1px solid ${alpha(theme.palette.primary.main, 0.15)}`,
                                height: '100%',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.15)}`,
                                  borderColor: alpha(theme.palette.primary.main, 0.3),
                                },
                              }}
                            >
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '1px',
                                  display: 'block',
                                  mb: 1,
                                }}
                              >
                                Charger Name
                              </Typography>
                              <Typography 
                                variant="body2" 
                                sx={{ 
                                  color: 'text.primary',
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  fontWeight: 600,
                                }}
                              >
                                {chargerDetails.chargepoint_name || chargerDetails.name}
                              </Typography>
                            </Box>
                          </Grid>
                        )}
                        {chargerDetails.status && (
                          <Grid item xs={6} sm={6}>
                            <Box
                              sx={{
                                p: { xs: 1.5, sm: 2 },
                                borderRadius: 2,
                                background: 'linear-gradient(135deg, rgba(102, 126, 234, 0.05) 0%, rgba(118, 75, 162, 0.05) 100%)',
                                border: '1px solid rgba(102, 126, 234, 0.15)',
                                height: '100%',
                                transition: 'all 0.3s ease',
                                '&:hover': {
                                  transform: 'translateY(-2px)',
                                  boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                                  borderColor: 'rgba(102, 126, 234, 0.3)',
                                },
                              }}
                            >
                              <Typography 
                                variant="caption" 
                                sx={{ 
                                  color: 'text.secondary',
                                  fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                  fontWeight: 600,
                                  textTransform: 'uppercase',
                                  letterSpacing: '1px',
                                  display: 'block',
                                  mb: 1,
                                }}
                              >
                                Status
                              </Typography>
                              <Chip
                                label={
                                  chargerDetails.status === 'A' ? 'Active' :
                                  chargerDetails.status === 'I' ? 'Inactive' :
                                  chargerDetails.status
                                }
                                size="medium"
                                color={
                                  chargerDetails.status === 'A' ||
                                  chargerDetails.status?.toLowerCase() === 'available' || 
                                  chargerDetails.status?.toLowerCase() === 'active' ||
                                  chargerDetails.status?.toLowerCase() === 'online'
                                    ? 'success'
                                    : chargerDetails.status?.toLowerCase() === 'charging' ||
                                      chargerDetails.status?.toLowerCase() === 'occupied'
                                    ? 'warning'
                                    : chargerDetails.status === 'I' ||
                                      chargerDetails.status?.toLowerCase() === 'offline' ||
                                      chargerDetails.status?.toLowerCase() === 'unavailable' ||
                                      chargerDetails.status?.toLowerCase() === 'inactive'
                                    ? 'error'
                                    : 'default'
                                }
                                sx={{ 
                                  fontWeight: 600,
                                  fontSize: { xs: '0.75rem', sm: '0.875rem' },
                                  height: { xs: 28, sm: 32 },
                                  minWidth: { xs: 90, sm: 110 },
                                }}
                              />
                            </Box>
                          </Grid>
                        )}
                      </Grid>
                    </CardContent>
                  </DetailCard>
                  
                  {/* Tabs for Connectors */}
                  {chargerDetails.connectors && chargerDetails.connectors.length > 0 && (
                    <Box sx={{ mt: 4 }}>
                      <TabContext value={selectedTab.toString()}>
                        <Box sx={{ 
                          width: '100%',
                          '& .MuiTabs-root': {
                            paddingBottom: '0 !important',
                            '& .MuiTabs-flexContainer': {
                              marginBottom: 0,
                            },
                          }
                        }}>
                        <EnhancedTabs
                          value={selectedTab.toString()}
                          onChange={(e, newValue) => setSelectedTab(parseInt(newValue))}
                          variant="modern"
                          color="primary"
                        >
                          {chargerDetails.connectors
                            .sort((a, b) => {
                              const aId = a.connector_id || a.connectorId || a.id || 0;
                              const bId = b.connector_id || b.connectorId || b.id || 0;
                              return Number(aId) - Number(bId);
                            })
                            .map((connector, index) => {
                              const connectorId = connector.connector_id || connector.connectorId || connector.id || index + 1;
                              const transaction = transactions[connectorId];
                              
                              // If charger status is N/A, mark connector status as N/A
                              const chargerStatus = chargerDetails.status?.toString().toUpperCase();
                              const connectorStatus = (chargerStatus === 'N/A' || chargerStatus === 'NA')
                                ? 'N/A'
                                : (connector.status || connector.connectorStatus || 'Unknown');
                              const getStatusColor = (status) => {
                                const statusLower = status?.toLowerCase();
                                if (statusLower === 'n/a' || statusLower === 'na') {
                                  return 'default';
                                } else if (statusLower === 'available') {
                                  return 'success';
                                } else if (
                                  statusLower === 'in use' ||
                                  statusLower === 'charging' ||
                                  statusLower === 'preparing'
                                ) {
                                  return 'warning';
                                } else if (
                                  statusLower === 'unavailable' ||
                                  statusLower === 'not available' ||
                                  statusLower === 'faulted'
                                ) {
                                  return 'error';
                                }
                                return 'default';
                              };

                              return (
                                <EnhancedTab
                                  key={connectorId}
                                  value={index.toString()}
                                  label={
                                    <Stack 
                                      direction={{ xs: 'column', sm: 'row' }} 
                                      spacing={{ xs: 0.5, sm: 1 }} 
                                      alignItems="center" 
                                      justifyContent="center"
                                    >
                                      <Typography sx={{ fontWeight: 600, fontSize: { xs: '0.75rem', sm: '0.875rem' } }}>
                                        Connector {connectorId}
                                      </Typography>
                                        <Chip
                                        label={connectorStatus}
                                          size="small"
                                        color={getStatusColor(connectorStatus)}
                                          sx={{ 
                                            height: 20, 
                                            fontSize: '0.65rem',
                                            fontWeight: 600,
                                            '& .MuiChip-label': {
                                              px: 0.75,
                                            }
                                          }}
                                        />
                                    </Stack>
                                  }
                                />
                              );
                            })}
                        </EnhancedTabs>
                        </Box>
                        
                        {/* Tab Panels */}
                        {chargerDetails.connectors
                          .sort((a, b) => {
                            const aId = a.connector_id || a.connectorId || a.id || 0;
                            const bId = b.connector_id || b.connectorId || b.id || 0;
                            return Number(aId) - Number(bId);
                          })
                          .map((connector, index) => {
                            const connectorId = connector.connector_id || connector.connectorId || connector.id || index + 1;
                            const transaction = transactions[connectorId];
                            const isLoading = transactionLoading[connectorId];
                            
                            return (
                              <TabPanel
                                key={connectorId}
                                value={index.toString()}
                                sx={{ 
                                  px: 0,
                                  py: 0,
                                  backgroundColor: theme.palette.grey[50],
                                  borderRadius: `0 0 ${theme.shape.borderRadius * 2}px ${theme.shape.borderRadius * 2}px`,
                                  mt: '-6px',
                                  '&.MuiTabPanel-root': {
                                    padding: 0,
                                    paddingTop: { xs: '16px', sm: '20px' },
                                    paddingLeft: { xs: '16px', sm: '24px' },
                                    paddingRight: { xs: '16px', sm: '24px' },
                                    paddingBottom: { xs: '16px', sm: '20px' },
                                  }
                                }}
                              >
                                {renderConnectorContent(connector, connectorId, isLoading)}
                              </TabPanel>
                            );
                          })}
                      </TabContext>
                    </Box>
                  )}
                  
                  {/* Single Connector Status (if connectors array not available but connector info exists) */}
                  {(!chargerDetails.connectors || chargerDetails.connectors.length === 0) && 
                   (chargerDetails.connectorId || chargerDetails.connectorStatus) && (
                    <DetailCard>
                      <CardContent sx={{ p: { xs: 2, sm: 2.5 } }}>
                        <Stack direction="row" spacing={1.5} alignItems="center" sx={{ mb: 2 }}>
                          <Box
                            sx={{
                              width: { xs: 40, sm: 48 },
                              height: { xs: 40, sm: 48 },
                              borderRadius: 1.5,
                              background: alpha(theme.palette.primary.main, 0.1),
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                            }}
                          >
                            <Icon 
                              icon={flashFill} 
                              width={24} 
                              height={24} 
                              style={{ color: theme.palette.primary.main }} 
                            />
                          </Box>
                          <Typography 
                            variant="h6" 
                            sx={{ 
                              fontWeight: 600, 
                              color: 'text.primary',
                              fontSize: { xs: '1rem', sm: '1.125rem' },
                              textAlign: 'left',
                            }}
                          >
                            Connector Status
                          </Typography>
                        </Stack>
                        
                        <Grid container spacing={{ xs: 1.5, sm: 2 }}>
                          {chargerDetails.connectorId && (
                            <Grid item xs={12} sm={6}>
                              <GridLabel>
                                <Box className="label">Connector ID</Box>
                                <Box className="value">{chargerDetails.connectorId}</Box>
                              </GridLabel>
                            </Grid>
                          )}
                          {chargerDetails.connectorStatus && (
                            <Grid item xs={12} sm={6}>
                              <GridLabel>
                                <Box className="label">Connector Status</Box>
                                <Box className="value" sx={{ mt: 0.5 }}>
                                  <Chip
                                    label={chargerDetails.connectorStatus}
                                    size="small"
                                    color={
                                      chargerDetails.connectorStatus?.toLowerCase() === 'available'
                                        ? 'success'
                                        : chargerDetails.connectorStatus?.toLowerCase() === 'charging' ||
                                          chargerDetails.connectorStatus?.toLowerCase() === 'preparing'
                                        ? 'warning'
                                        : chargerDetails.connectorStatus?.toLowerCase() === 'unavailable' ||
                                          chargerDetails.connectorStatus?.toLowerCase() === 'faulted'
                                        ? 'error'
                                        : 'default'
                                    }
                                    sx={{
                                      fontWeight: 600,
                                      fontSize: { xs: '0.7rem', sm: '0.75rem' },
                                      height: { xs: 24, sm: 28 },
                                      minWidth: { xs: 90, sm: 100 },
                                    }}
                                  />
                                </Box>
                              </GridLabel>
                            </Grid>
                          )}
                        </Grid>
                      </CardContent>
                    </DetailCard>
                  )}
                </Box>
              )}
            </CardContent>
          </SearchCard>
        )}


        {/* Stop Transaction Confirmation Dialog */}
        <Dialog
          open={stopDialogOpen}
          onClose={handleStopCancel}
          aria-labelledby="stop-dialog-title"
          aria-describedby="stop-dialog-description"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: 3,
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
            }
          }}
        >
          <DialogTitle 
            id="stop-dialog-title"
            sx={{
              fontWeight: 700,
              fontSize: '1.5rem',
              pb: 2,
              color: theme.palette.primary.main,
            }}
          >
            Confirm Stop Transaction
          </DialogTitle>
          <DialogContent>
            <DialogContentText 
              id="stop-dialog-description"
              sx={{
                textAlign: 'center',
                fontSize: '1rem',
                color: 'text.primary',
                fontWeight: 500,
                '& strong': {
                  color: theme.palette.primary.main,
                  fontWeight: 700,
                }
              }}
            >
              {stopTransactionId 
                ? `Are you sure you want to stop transaction ID: ${stopTransactionId}?`
                : ''}
            </DialogContentText>

            {stopError && (
              <Alert severity="error" sx={{ mt: 2, borderRadius: 2 }}>
                {stopError}
              </Alert>
            )}
          </DialogContent>
          <DialogActions sx={{ p: 3, pt: 2 }}>
            <Button 
              onClick={handleStopCancel} 
              disabled={stopping}
              sx={{
                fontWeight: 600,
                px: 3,
              }}
            >
              Cancel
            </Button>
            <Button
              onClick={handleStopConfirm}
              color="error"
              variant="contained"
                disabled={stopping}
              startIcon={stopping ? <CircularProgress size={16} color="inherit" /> : <Icon icon={stopCircleFill} width={18} height={18} />}
              sx={{
                fontWeight: 700,
                px: 3,
                boxShadow: '0 4px 12px rgba(211, 47, 47, 0.3)',
                '&:hover': {
                  boxShadow: '0 6px 16px rgba(211, 47, 47, 0.4)',
                },
                '&:disabled': {
                  backgroundColor: 'rgba(211, 47, 47, 0.3)',
                  color: 'rgba(255, 255, 255, 0.5)',
                },
              }}
            >
              {stopping ? 'Stopping...' : 'Submit'}
            </Button>
          </DialogActions>
        </Dialog>

        {/* Notification Setup Dialog */}
        <Dialog
          open={notificationDialogOpen}
          onClose={handleNotificationClose}
          aria-labelledby="notification-dialog-title"
          aria-describedby="notification-dialog-description"
          maxWidth="sm"
          fullWidth
          PaperProps={{
            sx: {
              borderRadius: { xs: 2, sm: 3 },
              boxShadow: '0 12px 40px rgba(0, 0, 0, 0.15)',
              m: { xs: 1, sm: 2 },
              maxHeight: { xs: '90vh', sm: 'auto' },
            }
          }}
        >
          <DialogTitle 
            id="notification-dialog-title"
            sx={{
              fontWeight: 700,
              fontSize: { xs: '1.25rem', sm: '1.5rem' },
              pb: { xs: 1.5, sm: 2 },
              px: { xs: 2, sm: 3 },
              pt: { xs: 2, sm: 3 },
              color: theme.palette.primary.main,
              display: 'flex',
              alignItems: 'center',
              gap: 1,
            }}
          >
            <Icon icon={bellFill} width={24} height={24} />
            <Typography sx={{ fontSize: { xs: '1.25rem', sm: '1.5rem' } }}>
              Receive Charging Updates
            </Typography>
          </DialogTitle>
          <DialogContent sx={{ px: { xs: 2, sm: 3 }, pb: { xs: 2, sm: 3 } }}>
            <DialogContentText 
              id="notification-dialog-description"
              sx={{
                textAlign: 'left',
                fontSize: { xs: '0.875rem', sm: '0.95rem' },
                color: 'text.secondary',
                mb: { xs: 2, sm: 3 },
                lineHeight: 1.6,
              }}
            >
              Enter your contact information to receive real-time updates about your charging session on Connector {notificationConnectorId}.
            </DialogContentText>
            
            {notificationSuccess ? (
              <Alert severity="success" sx={{ borderRadius: 2, mb: 2 }}>
                <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                  Notification setup successful! You will now receive charging updates on your email.
                </Typography>
              </Alert>
            ) : (
              <>
                {!otpSent ? (
                  <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                      <TextField
                        fullWidth
                        label="Email Address"
                        placeholder="Enter your email address (e.g., user@example.com)"
                        value={email}
                        onChange={handleEmailChange}
                        error={!!emailError}
                        helperText={emailError || 'Enter a valid email address'}
                        disabled={sendingOtp}
                        type="email"
                        size="small"
                        sx={{
                          '& .MuiOutlinedInput-root': {
                            borderRadius: 2,
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          },
                          '& .MuiInputLabel-root': {
                            fontSize: { xs: '0.875rem', sm: '1rem' },
                          },
                          '& .MuiFormHelperText-root': {
                            fontSize: { xs: '0.75rem', sm: '0.875rem' },
                          },
                        }}
                        inputProps={{
                          maxLength: 100,
                        }}
                      />
                  </Box>
                ) : (
                  <Box sx={{ mb: { xs: 1.5, sm: 2 } }}>
                    <Alert severity="info" sx={{ borderRadius: 2, mb: { xs: 1.5, sm: 2 } }}>
                      <Typography variant="body2" sx={{ fontWeight: 500, fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                        OTP has been sent to {email}. Please enter the 6-digit code below.
                      </Typography>
                    </Alert>
                    <TextField
                      fullWidth
                      label="Enter OTP"
                      placeholder="Enter 6-digit OTP"
                      value={otp}
                      onChange={handleOTPChange}
                      error={!!otpError}
                      helperText={otpError || 'Enter the 6-digit OTP sent to your email'}
                      disabled={verifyingOtp}
                      size="small"
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        },
                        '& .MuiInputLabel-root': {
                          fontSize: { xs: '0.875rem', sm: '1rem' },
                        },
                        '& .MuiFormHelperText-root': {
                          fontSize: { xs: '0.75rem', sm: '0.875rem' },
                        },
                      }}
                      inputProps={{
                        maxLength: 6,
                        inputMode: 'numeric',
                        pattern: '[0-9]*',
                      }}
                    />
                  </Box>
                )}

                {notificationError && (
                  <Alert severity="error" sx={{ mt: { xs: 1.5, sm: 2 }, borderRadius: 2 }}>
                    <Typography sx={{ fontSize: { xs: '0.875rem', sm: '0.9375rem' } }}>
                      {notificationError}
                    </Typography>
                  </Alert>
                )}
              </>
            )}
          </DialogContent>
          <DialogActions sx={{ 
            p: { xs: 2, sm: 3 }, 
            pt: { xs: 1, sm: 2 },
            flexDirection: { xs: 'column', sm: 'row' },
            gap: { xs: 1, sm: 0 },
            '& > *': {
              width: { xs: '100%', sm: 'auto' },
            }
          }}>
            {!notificationSuccess && (
              <>
                <Button 
                  onClick={handleNotificationClose} 
                  disabled={sendingOtp || verifyingOtp}
                  variant="outlined"
                  sx={{
                    fontWeight: 600,
                    px: { xs: 2, sm: 3 },
                    py: { xs: 1, sm: 1.25 },
                    fontSize: { xs: '0.875rem', sm: '1rem' },
                    order: { xs: 2, sm: 1 },
                  }}
                >
                  Cancel
                </Button>
                {!otpSent ? (
                  <Button
                    onClick={handleSendOTP}
                    color="primary"
                    variant="contained"
                    disabled={sendingOtp || !email || email.trim() === ''}
                    startIcon={sendingOtp ? <CircularProgress size={16} color="inherit" /> : <Icon icon={bellFill} width={18} height={18} />}
                    sx={{
                      fontWeight: 700,
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.25 },
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                      },
                      '&:disabled': {
                        backgroundColor: 'rgba(25, 118, 210, 0.3)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                      order: { xs: 1, sm: 2 },
                    }}
                  >
                    {sendingOtp ? 'Sending...' : 'Send OTP'}
                  </Button>
                ) : (
                  <Button
                    onClick={handleVerifyOTP}
                    color="primary"
                    variant="contained"
                    disabled={verifyingOtp || !otp || otp.length !== 6}
                    startIcon={verifyingOtp ? <CircularProgress size={16} color="inherit" /> : <Icon icon={checkmarkCircle2Fill} width={18} height={18} />}
                    sx={{
                      fontWeight: 700,
                      px: { xs: 2, sm: 3 },
                      py: { xs: 1, sm: 1.25 },
                      fontSize: { xs: '0.875rem', sm: '1rem' },
                      boxShadow: '0 4px 12px rgba(25, 118, 210, 0.3)',
                      '&:hover': {
                        boxShadow: '0 6px 16px rgba(25, 118, 210, 0.4)',
                      },
                      '&:disabled': {
                        backgroundColor: 'rgba(25, 118, 210, 0.3)',
                        color: 'rgba(255, 255, 255, 0.5)',
                      },
                      order: { xs: 1, sm: 2 },
                    }}
                  >
                    {verifyingOtp ? 'Verifying...' : 'Verify OTP'}
                  </Button>
                )}
              </>
            )}
          </DialogActions>
        </Dialog>
      </Container>
    </RootStyle>
  );
}

